import React, { FC, useContext, useMemo, RefObject } from 'react';

import classNames from 'classnames';
import { Button } from 'mintel-react-ui';
import { addToProject } from 'mintel-taskbar';
import { tablet } from 'mintel-tokens';
import { useMediaQuery } from 'react-responsive';

import BlockColumns from '/components/BlockColumns';
import { useColsCollapsed } from '/components/common';
import Context from '/components/Context';
import Footer from '/components/Footer';
import Title from '/components/Title';
import { ArticleBlockProps } from '/types';
import { Asset, assetUri } from '/utils/assetUri';
import {
  BlockMetadataContext,
  MetadataContext,
  PresentModeContext,
  PublishStatusContext,
} from '/utils/contexts';
import {
  createBlockProjectData,
  getSectionProjectData,
} from '/utils/functions';
import useTranslate from '/utils/translations';
import useBlockScaler from '/utils/useBlockScaler';
import { getContentTrackingPropertiesFromMetadata } from '/utils/useTracking';
import useUserAccessRestrictions from '/utils/useUserAccessRestrictions';

import styles from './ArticleBlock.scss';
import TableDownload from '../TableDownload/TableDownload';

const ArticleBlock: FC<ArticleBlockProps> = ({
  children,
  blockId,
  blockBackgroundImage = null,
  blockBackgroundType,
  blockContent,
  columns,
  columnWidth,
  contextContent,
  offset = 2,
  rowCenterAlign = false,
  presentCenterAlignDisabled = false,
  titleWithinColumn = false,
  noTitle = false,
  titleCentered = false,
  blockType,
  hideContext = false,
  hideFooter = false,
  sendSnackbarEvent,
  blocksProjectData,
  blockIndex,
  tableDownloadRef,
}) => {
  const articleJson = useContext(MetadataContext);
  const colsCollapsed = useColsCollapsed();
  const presentMode = useContext(PresentModeContext);
  const publishStatus = useContext(PublishStatusContext);
  const t = useTranslate();
  const backgroundImage =
    blockBackgroundImage &&
    blockBackgroundImage.startsWith('workspace_SpacesStore')
      ? assetUri(blockBackgroundImage, Asset.Image, publishStatus)
      : blockBackgroundImage;
  const border = blockContent?.border;
  const footnote = blockContent?.footnote;
  const base = blockContent?.base;
  const source = blockContent?.source;
  const blockRef = useBlockScaler(blockType, presentMode);
  const isSectionHeader = blockType === 'block-section-header';
  const shareLink = useMemo(
    () => `${window.location.href.split('#', 1)}#${blockId}`,
    [blockId],
  );
  const isSmallScreen = useMediaQuery({ maxWidth: tablet });

  const nonMyPresentationBlocks = [
    'block-analyst-feedback',
    'block-recommended-content',
  ];

  const nonCopyBlockLinkBlocks = [
    ...nonMyPresentationBlocks,
    'block-hero',
    'block-analyst',
  ];
  const accessRestrictions = useUserAccessRestrictions();

  const showLinkToBlockButton =
    !presentMode && !nonCopyBlockLinkBlocks.includes(blockType);

  const showAddToPresentationButton =
    accessRestrictions.isLoggedInUser &&
    !presentMode &&
    !isSmallScreen &&
    !nonMyPresentationBlocks.includes(blockType) &&
    !accessRestrictions.toolsDisabled &&
    !accessRestrictions.downloadDisabled &&
    !accessRestrictions.isLibraryLockdownUser;

  const addBlockToProject: VoidFunction = () => {
    const currentBlockData = createBlockProjectData(
      articleJson.id,
      blockId,
      blockType,
    );
    let projectData = [currentBlockData];
    if (isSectionHeader && blocksProjectData) {
      projectData = getSectionProjectData(blockId, blocksProjectData);
    }
    addToProject(projectData, {
      mixpanelProperties: getContentTrackingPropertiesFromMetadata(articleJson),
      mixpanelLocation: 'Content',
    });
  };

  const copyBlockLink: VoidFunction = () => {
    navigator.clipboard.writeText(shareLink);

    if (sendSnackbarEvent) {
      sendSnackbarEvent(t('Section link copied to Clipboard'));
    }
  };

  return (
    <BlockMetadataContext.Provider value={{ ...blockContent, shareLink }}>
      <section
        id={blockId}
        data-block-type={blockType}
        className={classNames(styles['ec-block'], styles[blockBackgroundType], {
          [styles['present-mode']]: !!presentMode,
        })}
        style={
          blockBackgroundImage
            ? { backgroundImage: `url(${backgroundImage})` }
            : {}
        }
      >
        <div className={styles[blockBackgroundType]}>
          <div
            className={classNames(styles['content-width-limit'], {
              [styles['present-mode']]: !!presentMode,
            })}
          >
            <div
              ref={blockRef as RefObject<HTMLDivElement>}
              id="article-block-element"
              className={classNames({
                [styles[`offset-${offset}`]]: !colsCollapsed,
                [styles[border || '']]: border,
                [styles['present-mode-flexbox']]: !!presentMode,
              })}
            >
              <div className={styles['block-buttons']}>
                {showLinkToBlockButton && (
                  <div
                    {...(blockIndex === 1
                      ? { 'data-intercom-target': 'cpa-share-link' }
                      : {})}
                  >
                    <Button.CopyLinkButton
                      handleClick={copyBlockLink}
                      ariaLabel={t('Copy link to section')}
                      tooltipPlacement="bottom"
                    />
                  </div>
                )}
                {!presentMode && tableDownloadRef && (
                  <TableDownload
                    tableRef={tableDownloadRef}
                    filename={`table_${articleJson.slug}_${
                      (blockIndex ?? 0) + 1
                    }.csv`}
                  />
                )}
                {showAddToPresentationButton &&
                  (isSectionHeader && blocksProjectData ? (
                    <Button.AddSectionToPresentationButton
                      handleClick={addBlockToProject}
                    />
                  ) : (
                    <div
                      {...(blockIndex === 1
                        ? { 'data-intercom-target': 'cpa-add-to-presentation' }
                        : {})}
                    >
                      <Button.AddToPresentationButton
                        handleClick={addBlockToProject}
                      />
                    </div>
                  ))}
              </div>
              {(!titleWithinColumn || colsCollapsed) && !noTitle && (
                <Title
                  centered={titleCentered}
                  content={blockContent?.title?.content}
                  hidden={blockContent?.title?.hidden}
                />
              )}
              {contextContent && (
                <div>
                  <Context content={contextContent} hide={hideContext} />
                </div>
              )}
              {columns &&
                blockContent.columns &&
                !blockContent?.columns?.hidden && (
                  <BlockColumns
                    columnComponents={columns}
                    layout={blockContent.columns.layout}
                    rowCenterAlign={rowCenterAlign}
                    columnWidth={columnWidth}
                    presentCenterAlignDisabled={presentCenterAlignDisabled}
                  />
                )}
              {children}
              {Boolean(footnote || base || source) && (
                <Footer
                  footnote={footnote}
                  base={base}
                  source={source}
                  hide={hideFooter}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </BlockMetadataContext.Provider>
  );
};

export default ArticleBlock;
